/**
 * API client for the school detail endpoint.
 *
 * Contract (TC-015): GET /api/v1/schools/:slug?sport=<slug>&target_year=<year>
 * Returns a SchoolDetail object.
 */

/**
 * Coarse-parent fallback score for a fine position when has_data is false.
 * Re-exported from api/search.ts shape. WI-26.
 */
export interface FallbackScore {
  score: number;
  position: string;
  position_name: string;
}

export interface PositionScore {
  position: string;       // "gk" | "def" | "mid" | "fwd" | "cb" | "lw" | etc.
  position_name: string;  // "Goalkeeper" | "Defender" | "Midfielder" | "Forward"
  score: number;          // 0–100
  tier: 'high' | 'medium' | 'low';
  tier_label: string;     // "HIGH" | "MED" | "LOW" (server-side canonical; use tierLabel() from api/search for display)
  has_data: boolean;      // true if ≥1 player is tagged with this position_id in the most recent
                          // roster snapshot. false if zero players are tagged. Used by
                          // HierarchicalScoreGrid to render "— no data" instead of a 0 badge.
  /**
   * Coarse parent score when has_data is false and this is a fine position.
   * Absent (undefined) for coarse positions and when has_data is true. WI-26.
   */
  fallback_score?: FallbackScore;
}

export interface CoachDetail {
  coach_id: string;
  name: string;
  title: string;
  email: string | null;
  phone: string | null;
  scraped_at: string;
}

export interface PlayerDetail {
  player_id: string;
  name: string;
  jersey_number: string | null;
  position: string;       // "gk" | "def" | "mid" | "fwd" | "unk"
  position_name: string;
  /** Coarse secondary position from a cross-line slash label (e.g. "D/M" → primary "def" + secondary "mid"). WI-30. */
  secondary_position: string | null;
  /** Coarse tertiary position from a triple-slash label (e.g. "D/M/F"). WI-30. */
  tertiary_position: string | null;
  class_year: 'FR' | 'SO' | 'JR' | 'SR' | 'GR';
  is_redshirt: boolean;
  hometown: string | null;
}

// ── New shapes for the school detail page redesign ──────────────────────────

/** SR/GR/JR/SO/FR player counts per coarse position group. */
export interface ClassCounts {
  SR: number;
  GR: number;
  JR: number;
  SO: number;
  FR: number;
}

/** Percentile rank + one-line descriptor within this school's division. */
export interface CompareInfo {
  /** 0–100 percentile (higher = more openings vs peers in same division). */
  percentile: number;
  /** e.g. "Top 9% of D2 MID scores", "Just above D2 median". */
  descriptor: string;
}

/** Fine-position breakdown for a coarse position card. */
export interface FineDetail {
  has_fine_data: boolean;
  /**
   * Present when has_fine_data = false.
   * List of fine labels the coarse score covers, e.g. ["CB", "LB", "RB"].
   */
  applies_to?: string[];
  /** Present when has_fine_data = true. */
  rows?: FineRowDetail[];
}

export interface FineRowDetail {
  slug: string;
  label: string;
  score: number | null;
  tier: 'high' | 'medium' | 'low' | null;
  no_data: boolean;
  class_counts: ClassCounts | null;
}

/** Per-coarse-position data shaped for the redesigned detail page. */
export interface PositionDetail {
  slug: string;
  label: string;
  long_label: string;
  score: number | null;
  tier: 'high' | 'medium' | 'low' | null;
  no_data: boolean;
  class_counts: ClassCounts;
  compare: CompareInfo | null;
  fine: FineDetail | null;
}

/** A single upcoming ID camp returned when include_camps=true. WI-48. */
export interface CampDetail {
  camp_id: string;
  name: string;
  start_date: string;          // "YYYY-MM-DD"
  end_date: string | null;     // "YYYY-MM-DD" or null for single-day events
  location_name: string | null;
  location_city: string | null;
  location_state: string | null;
  age_groups: string[];
  gender: 'mens' | 'womens' | 'coed';
  price_cents: number | null;
  registration_url: string | null;
  /**
   * URL of the page where this camp listing was found or is listed.
   * Promoted to public API in WI-50; is_archived remains internal.
   * Always present in the JSON response (value is null when not captured).
   */
  source_url: string | null;
  source: 'manual' | 'sidearm_camps' | 'scraper' | 'sidearm_camps_hub';
  /**
   * True when the camp has no confirmed date. Hub-link rows (source = 'sidearm_camps_hub')
   * always have date_unknown = true. start_date will be the sentinel "9999-12-31" and must
   * never be displayed. WI-51b.
   */
  date_unknown: boolean;
}

export interface SchoolDetail {
  school_id: string;
  school_name: string;
  school_slug: string;
  division: string;       // e.g. "ncaa_d1"
  division_name: string;  // e.g. "NCAA Division I"
  region: string;
  city: string;
  state: string;
  website_url: string | null;
  /** Athletic conference name. null until data backfill (migration 0014). */
  conference: string | null;
  sport: string;
  target_year: number;
  scraped_at: string | null;
  /**
   * Canonical URL of the live roster page that generated the score data.
   * null when no URL has been captured yet (school not yet scraped since WI-25a).
   * Always present in the JSON response (never absent) — use !== null check, not
   * truthiness, to distinguish "no URL" from "API bug". Added in WI-25b.
   */
  roster_source_url: string | null;
  /** New reshaped per-coarse-position array for the detail page redesign. */
  positions: PositionDetail[];
  /** Raw opening scores kept for backward compatibility. */
  opening_scores: PositionScore[];
  coaches: CoachDetail[];
  players: PlayerDetail[];
  /**
   * Upcoming ID camps for the requested sport. Present when include_camps=true
   * is passed to fetchSchoolDetail. Optional to avoid breaking existing test
   * mocks that don't include it. WI-48.
   */
  camps?: CampDetail[];
}

export interface SchoolDetailParams {
  slug: string;
  sport: string;
  target_year?: number;
  /** When true, appends include_camps=true to the request. WI-48. */
  include_camps?: boolean;
}

/**
 * Fetch school detail by slug. Throws on network error or non-2xx response.
 * Returns null on 404 (school not found).
 */
export async function fetchSchoolDetail(
  params: SchoolDetailParams
): Promise<SchoolDetail | null> {
  const query = new URLSearchParams({ sport: params.sport });
  if (params.target_year) {
    query.set('target_year', String(params.target_year));
  }
  if (params.include_camps) {
    query.set('include_camps', 'true');
  }
  const url = `/api/v1/schools/${encodeURIComponent(params.slug)}?${query.toString()}`;
  const response = await fetch(url);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`School detail fetch failed: ${response.status} ${response.statusText}`);
  }

  const data: SchoolDetail = await response.json();
  return data;
}

// ── Display helpers ────────────────────────────────────────────────────────────

/**
 * Background and border color classes for score tiles.
 *
 * Phase 1 fix: LOW tier changed from gray-50/gray-200 to red-50/red-200
 * to match the tier-low-bg and tier-low-border tokens.
 */
export function tierBgClass(tier: PositionScore['tier']): string {
  switch (tier) {
    case 'high':   return 'bg-green-50 border-green-200';
    case 'medium': return 'bg-amber-50 border-amber-200';
    case 'low':    return 'bg-red-50 border-red-200';
  }
}

/** Returns the tier icon character (circle fill state). */
export function tierIcon(tier: PositionScore['tier']): string {
  switch (tier) {
    case 'high':   return '●';
    case 'medium': return '◑';
    case 'low':    return '○';
  }
}

/**
 * Returns a "Last updated N days ago" label and a stale flag (> 30 days).
 * FR-014: stale threshold is 30 days.
 */
export function freshnessLabel(scrapedAt: string | null): {
  label: string;
  isStale: boolean;
} {
  if (!scrapedAt) {
    return { label: 'Last updated: unknown', isStale: false };
  }
  const scraped = new Date(scrapedAt);
  const now = new Date();
  const diffMs = now.getTime() - scraped.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const isStale = diffDays > 30;

  if (diffDays === 0) {
    return { label: 'Last updated today', isStale: false };
  }
  if (diffDays === 1) {
    return { label: 'Last updated 1 day ago', isStale: false };
  }
  return { label: `Last updated ${diffDays} days ago`, isStale };
}

/**
 * Compute the position-by-class-year summary table (TC-002 / FR-012).
 * Returns a map: position_slug -> { SR: n, GR: n, JR: n, SO: n, FR: n }
 */
export type ClassYearKey = 'SR' | 'GR' | 'JR' | 'SO' | 'FR';
export type PositionSummaryRow = Record<ClassYearKey, number>;

export function buildPositionSummary(
  players: PlayerDetail[]
): Map<string, PositionSummaryRow> {
  const summary = new Map<string, PositionSummaryRow>();

  for (const player of players) {
    const pos = player.position;
    if (pos === 'unk') continue; // skip unknown positions

    if (!summary.has(pos)) {
      summary.set(pos, { SR: 0, GR: 0, JR: 0, SO: 0, FR: 0 });
    }
    const row = summary.get(pos)!;
    const cy = player.class_year as ClassYearKey;
    if (cy in row) {
      row[cy] += 1;
    }
  }

  return summary;
}

/**
 * Normalise a phone string to a tel: href.
 * Strips all non-digit characters; prepends +1 for US numbers (10 digits).
 * Returns null if the string has no digits.
 */
export function normalizeTelHref(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 0) return null;
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  return `tel:${digits}`;
}

// ── School contexts (WI-29) ────────────────────────────────────────────────────

/**
 * Per-sport context for the SchoolLandingPicker.
 * has_data is true when opening_scores has at least one row for this (school, sport) pair.
 */
export interface SportContext {
  slug: string;
  name: string;
  has_data: boolean;
}

/**
 * Response shape for GET /api/v1/schools/:slug/contexts.
 * Drives the SchoolLandingPicker component.
 */
export interface SchoolContexts {
  school_name: string;
  slug: string;
  division: string;
  division_name: string;
  region: string;
  city: string;
  state: string;
  sports: SportContext[];
}

/**
 * Fetch sport contexts for a school by slug.
 * Returns null on 404 (school not found).
 * Throws on network error or 5xx.
 */
export async function fetchSchoolContexts(
  slug: string,
  signal?: AbortSignal
): Promise<SchoolContexts | null> {
  const url = `/api/v1/schools/${encodeURIComponent(slug)}/contexts`;
  const response = await fetch(url, { signal });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(
      `School contexts fetch failed: ${response.status} ${response.statusText}`
    );
  }

  const data: SchoolContexts = await response.json();
  return data;
}
